/**
 * Tab «Carichi previsti» (M3) — il tabellone: avviso di ritiro → risposta
 * cliente → NDP → **partenza**. La riga resta qui finché la merce non esce,
 * come nel foglio Excel di Marina.
 *
 * Da agosto 2026 non è più solo osservazione: Marina segna il giorno di
 * partenza (serve NDP o DDT) e da lì la riga finisce in cima e sul tabellone
 * Partenze che guarda il presidente. Nessun documento fiscale, nessun invio.
 */

import {
  AlertTriangle,
  Bell,
  Check,
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  Clock,
  Eye,
  File,
  FileCheck2,
  HelpCircle,
  Loader2,
  Search,
  Truck,
  Upload,
  Users,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Skeleton } from "@/components/ui/skeleton";
import {
  useCarichiPrevisti,
  useCpPartenza,
  useCpPatch,
  useM3DocumentiUpload,
} from "@/hooks/useM3";
import type { AvvioChat } from "@/components/m3/AidaSidebar";
import { CaricoPrevistoDrawer } from "@/components/m3/CaricoPrevistoDrawer";
import {
  cercaRighe,
  eCongelato,
  eUscita,
  NDP_LABEL,
  StatoBadge,
} from "@/components/m3/stato-carico";
import { cn } from "@/lib/utils";
import type {
  CaricoPrevistoPatch,
  CaricoPrevistoRow,
  DocumentiUploadResponse,
  StatoCaricoPrevisto,
} from "@/types/api";

/**
 * I cinque riquadri della testata. Non sono KPI: sono le code di lavoro di
 * Marina, e ognuno è insieme il numero e il filtro che lo apre.
 *
 * Prima erano due fasce distinte — la striscia KPI che informava e i chip che
 * filtravano. Renderle entrambe cliccabili avrebbe creato due meccanismi di
 * filtro e la domanda «quale è attivo?»; fonderle toglie una fascia e aggiunge
 * funzioni. Fuori «Clienti in attesa» (informativo, quasi uguale a «Tutti») e il
 * **lead time medio**, che è un dato direzionale e va nel cruscotto M4.
 */
type Filtro = "attivi" | "da_sollecitare" | "con_domande" | "da_confermare_uscita" | "usciti";

const RIQUADRI: { id: Filtro; label: string; icona: typeof Users; urgente?: boolean }[] = [
  { id: "attivi", label: "Tutti", icona: Users },
  { id: "da_sollecitare", label: "Da sollecitare", icona: Bell, urgente: true },
  { id: "con_domande", label: "Con domande", icona: HelpCircle },
  { id: "da_confermare_uscita", label: "Da confermare uscita", icona: Truck },
  { id: "usciti", label: "Usciti", icona: Check },
];

/**
 * Bandiera dal codice ISO2 (lettere → regional indicator symbols).
 *
 * Le sigle dell'anagrafica sono codici targa, non ISO: la traduzione sta in
 * `m3.paese.iso2`, ricavata incrociando ogni sigla con la città dei clienti. Chi
 * non ha corrispondenza mostra un trattino — 30 righe su 121 non hanno il cliente
 * in anagrafica, e dirlo è meglio che mettere una bandiera indovinata.
 */
function Bandiera({ iso2, nome }: { iso2: string | null; nome: string | null }) {
  if (!iso2) {
    return (
      <span
        className="w-[19px] shrink-0 text-center text-[11px] font-semibold text-muted-foreground/60"
        title="Paese non risolto"
      >
        —
      </span>
    );
  }
  const emoji = [...iso2.toUpperCase()]
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join("");
  return (
    <span className="shrink-0 text-[17px] leading-none" title={nome ?? iso2}>
      {emoji}
    </span>
  );
}

/**
 * Colonna «Attenzione»: un solo segnale per riga, il più urgente.
 *
 * Sono tre cose diverse — *mi manca un dato tuo* / *non so di chi è* / *non hai
 * validato la mia lettura* — ma mostrarle tutte insieme produce solo «c'è
 * qualcosa che non va». Una colonna, una gerarchia, un posto dove guardare.
 */
function Attenzione({ row }: { row: CaricoPrevistoRow }) {
  if (row.domanda) {
    const campi = row.domanda.campi.map((c) => c.label).join(", ");
    return (
      <span
        title={`Aida ha chiesto: ${campi || row.domanda.testo || "un dato"} — da ${row.domanda.giorni_aperta} gg`}
        className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[11px] font-bold text-amber-800"
      >
        <HelpCircle className="h-3.5 w-3.5" />
        {row.domanda.campi.length > 1 && row.domanda.campi.length}
      </span>
    );
  }
  if (row.match_incerto) {
    return (
      <span title="Cliente incerto: non sappiamo di chi è questa riga">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
      </span>
    );
  }
  if (row.da_verificare) {
    return (
      <span title="Da verificare: Aida ha letto, nessuno ha confermato">
        <Eye className="h-4 w-4 text-muted-foreground" />
      </span>
    );
  }
  return <span className="text-muted-foreground">·</span>;
}

/**
 * Cella compilabile direttamente in tabella — l'approccio «Excel» chiesto da
 * Enrico, ma mirato: solo i campi che Aida può chiedere, e senza trasformare
 * ogni cella in un input (una tabella tutta di input non si legge più).
 *
 * Tre stati visivi: **accesa** se il dato manca ed è ciò che Aida sta aspettando,
 * tratteggiata se manca e nessuno l'ha chiesto, testo normale se c'è. Il click
 * apre l'editing in loco e non apre il drawer.
 *
 * Salvataggio automatico all'uscita dal campo, ma **dichiarato**: «salvato» che
 * compare e sfuma, rosso se fallisce. Senza il segnale non si capisce di aver
 * fatto qualcosa — è il difetto che la prova ha trovato.
 */
function CellaCampo({
  rowId,
  chiave,
  valore,
  tipo,
  atteso,
  fonte,
}: {
  rowId: string;
  chiave: "data_ritiro" | "pronto_dal" | "pallet";
  valore: string | null;
  tipo: "date" | "number";
  atteso: boolean;
  /** Provenienza del valore: si mostra solo se NON è di Marina — un dato che ha
   *  scritto lei non ha bisogno di etichetta, uno messo da altri sì. */
  fonte?: string;
}) {
  const patch = useCpPatch(rowId);
  const [modifica, setModifica] = useState(false);
  const [bozza, setBozza] = useState(valore ?? "");
  const [salvato, setSalvato] = useState(false);

  useEffect(() => setBozza(valore ?? ""), [valore]);
  useEffect(() => {
    if (!salvato) return;
    const t = setTimeout(() => setSalvato(false), 1800);
    return () => clearTimeout(t);
  }, [salvato]);

  function salva(v: string | null) {
    setModifica(false);
    if ((v ?? "") === (valore ?? "")) return;
    const payload: CaricoPrevistoPatch =
      v === null || v === ""
        ? { [chiave]: null }
        : { [chiave]: tipo === "number" ? Number(v) : v };
    patch.mutate(payload, { onSuccess: () => setSalvato(true) });
  }

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  if (modifica) {
    return (
      <td className="bg-brand-50/60 px-2.5 py-2" onClick={stop}>
        <input
          type={tipo}
          autoFocus
          step={tipo === "number" ? "1" : undefined}
          min={tipo === "number" ? "0" : undefined}
          value={bozza}
          onChange={(e) => setBozza(e.target.value)}
          onBlur={() => salva(bozza)}
          onKeyDown={(e) => {
            if (e.key === "Enter") salva(bozza);
            if (e.key === "Escape") {
              setBozza(valore ?? "");
              setModifica(false);
            }
          }}
          className="h-7 w-full min-w-[7rem] rounded border border-brand-400 bg-card px-1.5 text-xs"
        />
      </td>
    );
  }

  const mostrato =
    valore == null || valore === ""
      ? null
      : tipo === "date"
        ? new Date(valore).toLocaleDateString("it-IT")
        : Math.round(Number(valore));

  const etichettaFonte =
    fonte && fonte !== "marina" ? { ndp: "NDP", aida: "Aida", m1: "sistema" }[fonte] : null;

  return (
    <td className="group bg-brand-50/60 px-2.5 py-2" onClick={stop}>
      <span className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => setModifica(true)}
          disabled={patch.isPending}
          title={mostrato == null ? "Clicca per compilare" : "Clicca per modificare"}
          className={cn(
            "rounded px-1.5 py-0.5 text-left text-xs transition-colors",
            tipo === "number" && "tabular-nums",
            mostrato == null
              ? atteso
                ? "bg-amber-100 font-medium text-amber-900 ring-1 ring-inset ring-amber-300 hover:bg-amber-200"
                : "min-w-[3rem] text-muted-foreground/70 ring-1 ring-inset ring-border hover:bg-muted"
              : "hover:bg-muted",
            patch.isPending && "opacity-50",
          )}
        >
          {mostrato ?? (atteso ? "manca" : "—")}
        </button>
        {etichettaFonte && (
          <span className="shrink-0 text-[9.5px] text-muted-foreground" title={`Valore da ${etichettaFonte}`}>
            {etichettaFonte}
          </span>
        )}
        {mostrato != null && (
          <button
            type="button"
            onClick={() => salva(null)}
            title="Svuota il campo"
            className="opacity-0 transition-opacity group-hover:opacity-100"
          >
            <X className="h-3 w-3 text-muted-foreground hover:text-red-600" />
          </button>
        )}
        {salvato && <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />}
        {patch.isError && (
          <span title="Salvataggio non riuscito">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-red-600" />
          </span>
        )}
      </span>
    </td>
  );
}

/** Giorni di attesa del cliente: vivi se la riga è aperta, congelati alla NDP. */
function giorniTempo(row: CaricoPrevistoRow): number | null {
  if (row.genesi === "ndp" || !row.avvisato_il) return null;
  return eCongelato(row.stato) && row.lead_time_giorni != null
    ? row.lead_time_giorni
    : (Date.now() - new Date(row.avvisato_il).getTime()) / 86_400_000;
}

/** Tempo cliente vivo: giorni da `avvisato_il` (righe aperte), o congelato. */
function useTempoCliente(row: CaricoPrevistoRow): string {
  const [, tick] = useState(0);
  const chiuso = eCongelato(row.stato);
  useEffect(() => {
    if (chiuso || !row.avvisato_il) return;
    const t = setInterval(() => tick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, [chiuso, row.avvisato_il]);
  const g = giorniTempo(row);
  return g == null ? "n/d" : `${g.toFixed(1)} gg`;
}

function Riga({ row, onClick }: { row: CaricoPrevistoRow; onClick: () => void }) {
  const tempo = useTempoCliente(row);
  const chiuso = eCongelato(row.stato);
  // I campi che Aida sta aspettando su questa riga: sono quelli che si accendono.
  const attesi = new Set((row.domanda?.campi ?? []).map((c) => c.chiave));
  return (
    <tr
      className={cn(
        "cursor-pointer border-b hover:bg-muted/40",
        row.domanda && "bg-amber-50/40",
        eUscita(row.stato) && "text-muted-foreground",
      )}
      onClick={onClick}
    >
      <td className="px-2 py-2"><Attenzione row={row} /></td>

      {/* Cliente: è la chiave con cui Marina cerca la riga, quindi domina.
          Bandiera dalla sigla dell'anagrafica tradotta in ISO. */}
      <td className="max-w-[15rem] px-2.5 py-2">
        <span className="flex items-center gap-2">
          <Bandiera iso2={row.paese_iso2} nome={row.paese_nome} />
          {row.cliente_ignoto ? (
            <span className="rounded-full border border-dashed border-red-400 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">
              Cliente da identificare
            </span>
          ) : (
            <span className="min-w-0" title={row.cliente_nome}>
              <span
                className={cn(
                  "block truncate text-[13.5px] font-semibold leading-tight tracking-tight",
                  eUscita(row.stato) && "font-medium text-muted-foreground",
                )}
              >
                {row.cliente_nome}
              </span>
              {(row.paese_nome || row.intermediario) && (
                <span className="block truncate text-[10.5px] text-muted-foreground">
                  {row.paese_nome}
                  {row.intermediario && ` · via ${row.intermediario}`}
                </span>
              )}
            </span>
          )}
        </span>
      </td>

      {/* Commessa + riferimenti: erano due colonne, ma sono la stessa cosa a due
          livelli di certezza — la commessa nostra e come la chiama il cliente. */}
      <td className="px-2.5 py-2">
        <span className="font-semibold tabular-nums">
          {row.commesse.length ? row.commesse.join(", ") : <span className="font-normal text-muted-foreground">—</span>}
        </span>
        {row.riferimenti_cliente.length > 0 && (
          <span className="block max-w-[9rem] truncate text-[10.5px] text-muted-foreground">
            {row.riferimenti_cliente.join(" · ")}
          </span>
        )}
      </td>

      <td className="px-2.5 py-2"><StatoBadge stato={row.stato} /></td>

      {/* I tre campi compilabili, su fondo proprio: è l'unica zona in cui si
          SCRIVE, e raggrupparla la rende riconoscibile senza dover trasformare
          ogni cella della tabella in un input. */}
      <CellaCampo
        rowId={row.id}
        chiave="data_ritiro"
        valore={row.data_ritiro}
        tipo="date"
        atteso={attesi.has("data_ritiro")}
        fonte={row.campi_fonte?.data_ritiro?.fonte}
      />
      <CellaCampo
        rowId={row.id}
        chiave="pronto_dal"
        valore={row.pronto_dal}
        tipo="date"
        atteso={attesi.has("pronto_dal")}
        fonte={row.campi_fonte?.pronto_dal?.fonte}
      />
      <CellaCampo
        rowId={row.id}
        chiave="pallet"
        valore={row.pallet_previsti != null ? String(row.pallet_previsti) : null}
        tipo="number"
        atteso={attesi.has("pallet")}
        fonte={row.campi_fonte?.pallet?.fonte}
      />

      {/* NDP: il numero, non «Emessa». Quello lo diceva già il badge di stato,
          mentre il numero serve quando si cerca un documento e non si vedeva. */}
      <td className="px-2.5 py-2">
        {row.ndp_numero ? (
          <span className="inline-flex items-center gap-1.5 tabular-nums" title="NDP emessa">
            <FileCheck2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
            {row.ndp_numero}
          </span>
        ) : (
          <span title={NDP_LABEL[row.ndp_badge] ?? row.ndp_badge}>
            <File className="h-3.5 w-3.5 text-muted-foreground/50" />
          </span>
        )}
      </td>

      <CellaPartenza row={row} />

      {/* «Avvisato il» è rientrato qui: era una colonna per un dato che si legge
          meglio come «da quanto aspetta». La data resta nel tooltip. */}
      <td className="px-2.5 py-2 tabular-nums">
        <span
          title={
            row.avvisato_il
              ? `Avvisato il ${new Date(row.avvisato_il).toLocaleDateString("it-IT")} ${
                  row.canale_avviso === "telefono" ? "(telefono)" : "(email)"
                }`
              : undefined
          }
          className={cn(
            "inline-flex items-center gap-1",
            !chiuso && row.stato === "sollecito_dovuto" && "font-semibold text-red-600",
          )}
        >
          {!chiuso && row.avvisato_il && <Clock className="h-3.5 w-3.5" />}
          {tempo}
        </span>
      </td>
      <td className="max-w-[10rem] truncate px-2.5 py-2 text-xs text-muted-foreground">
        {row.note || "—"}
      </td>
    </tr>
  );
}

/**
 * Colonna «Partenza» — l'uscita è un FATTO che si registra, non un permesso che
 * si chiede.
 *
 * Prima l'azione stava nel drawer e le parole («segna il giorno di partenza»,
 * «confermo l'uscita») facevano sembrare che il sistema autorizzasse la merce a
 * partire. Ora: un click sulla riga, «è uscito», adesso. Il giorno previsto resta
 * solo per pianificare in avanti, dal drawer.
 *
 * Il pulsante compare **appena c'è la NDP**: Enrico ha portato la regola di
 * dominio che mi mancava — la nota di prelievo si emette poco prima che il camion
 * esca, quindi è già il segnale che la partenza è imminente.
 *
 * La strada definitiva resta la riconciliazione col DDT — il documento che
 * accompagna la merce che esce è già la prova che è partita, e porta anche la data
 * giusta. Il backend è predisposto (`riconcilia_uscita_da_ddt`); questo pulsante
 * serve per quando il DDT arriva dopo, o non arriva.
 */
function CellaPartenza({ row }: { row: CaricoPrevistoRow }) {
  const partenza = useCpPartenza(row.id);
  const oggi = new Date().toISOString().slice(0, 10);

  if (row.partito_il) {
    return (
      <td className="px-2.5 py-2">
        <span
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700"
          title={`Uscita il ${new Date(row.partito_il).toLocaleDateString("it-IT")}`}
        >
          <Truck className="h-3.5 w-3.5" />
          Uscito
        </span>
      </td>
    );
  }

  if (!row.puo_confermare_uscita) {
    // Manca la NDP (o il DDT): senza documento non c'è niente da confermare.
    return (
      <td className="px-2.5 py-2">
        <span
          className="text-xs text-muted-foreground"
          title="Serve la NDP (o il DDT)"
        >
          {row.data_partenza_prevista
            ? new Date(row.data_partenza_prevista).toLocaleDateString("it-IT")
            : "—"}
        </span>
      </td>
    );
  }

  const previstoOggi = row.data_partenza_prevista === oggi;
  return (
    <td className="px-2.5 py-2" onClick={(e) => e.stopPropagation()}>
      <span className="flex items-center gap-1.5">
        {row.data_partenza_prevista && !previstoOggi && (
          <span className="text-[11px] text-muted-foreground">
            {new Date(row.data_partenza_prevista).toLocaleDateString("it-IT")}
          </span>
        )}
        <button
          type="button"
          disabled={partenza.isPending}
          onClick={() => partenza.mutate({ uscito: true })}
          title="Registra che il camion è uscito"
          // PIENO, non bordato: in una riga fatta di badge pallidi un chip
          // bordato È un badge, e non si legge come comando. Il colore pieno lo
          // rende l'unico elemento cliccabile della riga.
          className={cn(
            "inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-white",
            "shadow-sm transition-colors disabled:opacity-50",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            previstoOggi
              ? "bg-amber-600 hover:bg-amber-700"
              : "bg-brand-700 hover:bg-brand-800",
          )}
        >
          <Truck className="h-3.5 w-3.5" />
          Conferma uscita
        </button>
      </span>
    </td>
  );
}

// ── Ordinamento e ricerca (client-side) ──────────────────────────────
//
// Client-side perché le righe arrivano in un solo payload: l'ordinamento è
// istantaneo e il backend non si tocca.
// ⚠️ Regge finché il tabellone sta in una risposta sola. Oltre il migliaio di
// righe va spostato sul server, insieme alla ricerca.

/** Solo le colonne che hanno un ordine sensato: un'intestazione che sembra
 *  cliccabile ma non lo è è peggio di una normale. Fuori Attenzione, Uscita, Note. */
type Colonna =
  | "cliente"
  | "commessa"
  | "stato"
  | "data_ritiro"
  | "pronto_dal"
  | "pallet"
  | "ndp"
  | "tempo";

type Ordine = { col: Colonna; desc: boolean } | null;

/** Ordine di ciclo di vita, non alfabetico: «Sollecitare» in cima vale più di
 *  «Allineato DDT» prima di «Confermato». */
const ORDINE_STATO: Record<StatoCaricoPrevisto, number> = {
  sollecito_dovuto: 0,
  in_attesa: 1,
  interlocutoria: 2,
  posticipato: 3,
  confermato: 4,
  ndp_emessa: 5,
  allineato_ddt: 6,
  partito: 7,
};

function valoreCol(row: CaricoPrevistoRow, col: Colonna): string | number | null {
  switch (col) {
    case "cliente":
      return row.cliente_nome?.toLowerCase() ?? null;
    case "commessa":
      return row.commesse.length ? row.commesse[0] : null;
    case "stato":
      return ORDINE_STATO[row.stato];
    case "data_ritiro":
      return row.data_ritiro;
    case "pronto_dal":
      return row.pronto_dal;
    case "pallet":
      return row.pallet_previsti;
    case "ndp":
      return row.ndp_numero?.toLowerCase() ?? null;
    case "tempo":
      return giorniTempo(row);
  }
}

function ordina(items: CaricoPrevistoRow[], ord: Ordine): CaricoPrevistoRow[] {
  if (!ord) return items;
  const segno = ord.desc ? -1 : 1;
  return [...items].sort((a, b) => {
    const va = valoreCol(a, ord.col);
    const vb = valoreCol(b, ord.col);
    // I vuoti restano in fondo in entrambi i versi: una colonna di trattini in
    // cima non è un ordinamento, è rumore.
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    return va < vb ? -segno : va > vb ? segno : 0;
  });
}

/**
 * Cerca su nome cliente · commessa · NDP · **riferimenti cliente**.
 *
 * I riferimenti sono la parte che serve davvero: Marina cerca quello che scrive
 * il cliente — `26IT0992`, `ORDER NO 742`, `PO 18` — e senza quelli la ricerca
 * fallisce proprio nei casi in cui la si usa.
 */
function IntestazioneOrd({
  col,
  label,
  ord,
  onOrd,
  className,
}: {
  col: Colonna;
  label: string;
  ord: Ordine;
  onOrd: (c: Colonna) => void;
  className?: string;
}) {
  const attiva = ord?.col === col;
  return (
    <th className={cn("px-2.5 py-2 font-medium", className)}>
      <button
        type="button"
        onClick={() => onOrd(col)}
        className="inline-flex items-center gap-1 hover:text-foreground"
        title={`Ordina per ${label.toLowerCase()}`}
      >
        {label}
        {attiva ? (
          ord.desc ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronUp className="h-3 w-3" />
          )
        ) : (
          <ChevronsUpDown className="h-3 w-3 opacity-30" />
        )}
      </button>
    </th>
  );
}

/** Un riquadro della testata: il numero È il filtro. */
function Riquadro({
  icona: Icona,
  label,
  valore,
  attivo,
  urgente,
  onClick,
}: {
  icona: typeof Users;
  label: string;
  valore: number | undefined;
  attivo: boolean;
  urgente?: boolean;
  onClick: () => void;
}) {
  const vuoto = valore === 0;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={attivo}
      className={cn(
        "rounded-lg border p-3 text-left transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        attivo
          ? "border-brand-700 bg-brand-50 ring-1 ring-brand-700"
          : "bg-card hover:border-brand-400 hover:bg-brand-50/50",
        // Una coda vuota non è un errore: si spegne invece di sparire, così la
        // testata non cambia forma sotto le mani di Marina.
        vuoto && !attivo && "opacity-60",
      )}
    >
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icona className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </div>
      <div
        className={cn(
          "mt-1 text-2xl font-bold tabular-nums",
          urgente && !vuoto && "text-red-600",
        )}
      >
        {valore ?? "—"}
      </div>
    </button>
  );
}

/**
 * Il riquadro di rilascio: NDP, DDT e fattura si caricano da qui, senza cambiare
 * tab.
 *
 * Riquadro **visibile** e non bersaglio invisibile su tutta l'area (scelta di
 * Enrico): un'area di drop che non si vede non si usa, e chi non trascina non ha
 * modo di scoprirla. Riusa lo **stesso endpoint** della sezione Documenti —
 * due pipeline divergerebbero al primo bug.
 */
function RiquadroRilascio({
  onRiga,
  onDocumenti,
}: {
  /** Porta alla riga del tabellone che il documento ha cambiato. */
  onRiga: (caricoId: string) => void;
  /** Manda alla sezione Documenti, dove c'è la coda «assegna a carico». */
  onDocumenti: () => void;
}) {
  const upload = useM3DocumentiUpload();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [esito, setEsito] = useState<DocumentiUploadResponse | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  function invia(file: File) {
    setEsito(null);
    setErrore(null);
    upload.mutate(
      { file },
      {
        onSuccess: setEsito,
        onError: (e: unknown) =>
          setErrore(e instanceof Error ? e.message : "Caricamento non riuscito."),
      },
    );
  }

  const caricoEsito = esito?.pacco?.carico_id ?? null;
  return (
    <div className="w-full sm:w-[26rem]">
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) invia(file);
          e.target.value = "";
        }}
      />
      <div
        role="button"
        tabIndex={0}
        onClick={() => !upload.isPending && inputRef.current?.click()}
        onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files?.[0];
          if (file) invia(file);
        }}
        className={cn(
          "flex h-[38px] cursor-pointer items-center gap-2 rounded-md border-[1.5px] border-dashed px-3 transition-colors",
          dragOver
            ? "border-brand-600 bg-brand-50"
            : "border-border hover:border-brand-400 hover:bg-brand-50/50",
        )}
      >
        {upload.isPending ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-brand-700" />
        ) : (
          <Upload className="h-4 w-4 shrink-0 text-brand-700" />
        )}
        <span className="truncate text-xs text-muted-foreground">
          {upload.isPending
            ? "Aida sta leggendo il documento…"
            : "Rilascia qui NDP, DDT o fattura"}
        </span>
      </div>

      {/* L'esito: cosa è cambiato, e la strada per andarci. Un documento che
          sparisce senza dire niente è il modo più veloce per perdere fiducia. */}
      {(esito || errore) && (
        <div
          className={cn(
            "mt-1.5 flex items-start gap-1.5 rounded-md px-2 py-1.5 text-[11px]",
            errore || esito?.esito === "non_riconosciuto"
              ? "bg-red-50 text-red-700"
              : esito?.esito === "in_coda_match"
                ? "bg-amber-50 text-amber-800"
                : "bg-emerald-50 text-emerald-800",
          )}
        >
          <span className="min-w-0 flex-1">
            {errore ?? esito?.messaggio}
            {caricoEsito && (
              <button
                type="button"
                onClick={() => onRiga(caricoEsito)}
                className="ml-1 font-semibold underline underline-offset-2"
              >
                Vai alla riga
              </button>
            )}
            {esito?.esito === "in_coda_match" && (
              <button
                type="button"
                onClick={onDocumenti}
                className="ml-1 font-semibold underline underline-offset-2"
              >
                Assegnalo dalla sezione Documenti
              </button>
            )}
          </span>
          <button
            type="button"
            onClick={() => {
              setEsito(null);
              setErrore(null);
            }}
            className="shrink-0 rounded p-0.5 hover:bg-black/5"
            aria-label="Chiudi"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}

export function CarichiPrevistiView({
  onApriChat,
}: {
  /** Apre la sidebar di Aida sulla domanda della riga. Senza, la frase «rispondi
   *  dalla chat» sarebbe un vicolo cieco: era il caso (5) trovato alla prova. */
  onApriChat?: (avvio: AvvioChat) => void;
} = {}) {
  const [filtro, setFiltro] = useState<Filtro>("attivi");
  const [ricerca, setRicerca] = useState("");
  const [ord, setOrd] = useState<Ordine>(null);
  const [selId, setSelId] = useState<string | null>(null);
  const [, setSearchParams] = useSearchParams();

  // La ricerca AZZERA il filtro e cerca su tutto il tabellone, uscite comprese:
  // un meccanismo attivo alla volta, e nessun «non lo trovo» perché la riga era
  // filtrata altrove.
  const cercando = ricerca.trim().length > 0;
  const { data, isLoading } = useCarichiPrevisti(cercando ? "tutti" : filtro);
  const conteggi = data?.conteggi;

  const items = ordina(cercaRighe(data?.items ?? [], ricerca), ord);

  function cambiaOrd(col: Colonna) {
    // Terzo click: si torna all'ordine di immissione, che è il default.
    setOrd((o) => (o?.col !== col ? { col, desc: false } : o.desc ? null : { col, desc: true }));
  }

  return (
    <div className="space-y-4">
      {/* La testata: cinque riquadri che sono insieme il numero e il filtro. */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {RIQUADRI.map((r) => (
          <Riquadro
            key={r.id}
            icona={r.icona}
            label={r.label}
            valore={conteggi?.[r.id]}
            urgente={r.urgente}
            // Mentre si cerca nessun riquadro è acceso: il filtro non è più in gioco.
            attivo={!cercando && filtro === r.id}
            onClick={() => {
              setRicerca("");
              setFiltro(r.id);
            }}
          />
        ))}
      </div>

      {/* Ricerca + rilascio documenti: la seconda e ultima fascia. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={ricerca}
            onChange={(e) => setRicerca(e.target.value)}
            placeholder="Cerca cliente, commessa, NDP o il riferimento del cliente…"
            className={cn(
              "h-[38px] w-full rounded-md border bg-card pl-8 pr-8 text-sm",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            )}
          />
          {cercando && (
            <button
              type="button"
              onClick={() => setRicerca("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted"
              aria-label="Pulisci la ricerca"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <RiquadroRilascio
          onRiga={(caricoId) => {
            const riga = (data?.items ?? []).find((r) => r.carico_id === caricoId);
            if (riga) setSelId(riga.id);
          }}
          onDocumenti={() =>
            setSearchParams((p) => {
              const next = new URLSearchParams(p);
              next.set("tab", "documenti");
              return next;
            })
          }
        />
      </div>

      {/* In partenza oggi: la priorità del giorno, sopra tutto il resto.
          Sostituisce il foglio che Marina stampava per il presidente. */}
      <BandaPartenze items={items} onApri={setSelId} />

      {/* Tabella */}
      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : items.length === 0 ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          {cercando
            ? `Nessuna riga per «${ricerca.trim()}» — la ricerca guarda cliente, commessa, NDP e riferimenti del cliente.`
            : "Nessun carico previsto in questa coda."}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
              {/* Undici colonne invece di tredici: «Avvisato il» è rientrato in
                  «Tempo», i riferimenti sotto la commessa. */}
              {/* Ordinabili solo le colonne dati: Attenzione, Uscita e Note non
                  hanno un ordine sensato e restano intestazioni normali. */}
              <tr>
                <th className="px-2.5 py-2 font-medium" title="Cosa richiede la tua attenzione">
                  <span className="sr-only">Attenzione</span>
                  <HelpCircle className="h-3.5 w-3.5" />
                </th>
                <IntestazioneOrd col="cliente" label="Cliente" ord={ord} onOrd={cambiaOrd} />
                <IntestazioneOrd col="commessa" label="Commessa" ord={ord} onOrd={cambiaOrd} />
                <IntestazioneOrd col="stato" label="Stato" ord={ord} onOrd={cambiaOrd} />
                <IntestazioneOrd
                  col="data_ritiro" label="Ritiro" ord={ord} onOrd={cambiaOrd} className="bg-brand-50"
                />
                <IntestazioneOrd
                  col="pronto_dal" label="Pronto dal" ord={ord} onOrd={cambiaOrd} className="bg-brand-50"
                />
                <IntestazioneOrd
                  col="pallet" label="Pallet" ord={ord} onOrd={cambiaOrd} className="bg-brand-50"
                />
                <IntestazioneOrd col="ndp" label="NDP" ord={ord} onOrd={cambiaOrd} />
                <th className="px-2.5 py-2 font-medium">Uscita</th>
                <IntestazioneOrd col="tempo" label="Tempo" ord={ord} onOrd={cambiaOrd} />
                <th className="px-2.5 py-2 font-medium">Note</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <Riga key={row.id} row={row} onClick={() => setSelId(row.id)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selId && (
        <CaricoPrevistoDrawer
          id={selId}
          onClose={() => setSelId(null)}
          // Il drawer copre il dock di Aida e occupa lo stesso bordo destro: non
          // sono due pannelli da tenere aperti insieme, sono due modi di lavorare
          // sulla stessa riga. Quindi si chiude, e il contesto passa nella
          // conversazione — che è il motivo per cui Aida ripete la domanda.
          onApriChat={(avvio) => {
            setSelId(null);
            onApriChat?.(avvio);
          }}
        />
      )}
    </div>
  );
}

/** Le righe segnate in partenza oggi, in cima e ben visibili. */
function BandaPartenze({
  items,
  onApri,
}: {
  items: CaricoPrevistoRow[];
  onApri: (id: string) => void;
}) {
  const oggi = new Date().toISOString().slice(0, 10);
  const oggiInPartenza = items.filter((r) => r.data_partenza_prevista === oggi);
  if (oggiInPartenza.length === 0) return null;

  const pallet = oggiInPartenza.reduce((s, r) => s + (r.pallet_previsti ?? 0), 0);
  return (
    <div className="rounded-lg border border-brand-200 bg-brand-50/60 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-brand-700">
          <Truck className="h-4 w-4" />
          In partenza oggi ({oggiInPartenza.length})
        </h3>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span>{Math.round(pallet)} pallet</span>
          <Link to="/partenze" className="font-medium text-brand-700 hover:underline">
            Tabellone partenze →
          </Link>
        </div>
      </div>
      <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {oggiInPartenza.map((r) => (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => onApri(r.id)}
              className="w-full rounded-md border bg-card px-3 py-2 text-left hover:bg-muted"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-medium">{r.cliente_nome}</span>
                {r.partito_il ? (
                  <span className="shrink-0 text-[11px] font-medium text-emerald-700">uscita</span>
                ) : (
                  <span className="shrink-0 text-[11px] font-medium text-amber-700">da far uscire</span>
                )}
              </div>
              <div className="mt-0.5 text-[11px] text-muted-foreground">
                {r.commesse.length > 0 ? `commessa ${r.commesse.join(", ")}` : "—"}
                {r.pallet_previsti != null && ` · ${Math.round(r.pallet_previsti)} pallet`}
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
